
import { openDB, IDBPDatabase } from 'idb';
import { CacheManifest, CachedItem } from '../../types/cache';
import { computeHash } from '../hash';

const CACHE_DB_NAME = 'studio_smart_cache';
const CACHE_DB_VERSION = 1;
const MANIFEST_STORE = 'manifests';
const CONTENT_STORE = 'contents';

class SmartCacheManager {
  private dbPromise: Promise<IDBPDatabase> | null = null;

  private getDB() {
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = openDB(CACHE_DB_NAME, CACHE_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(MANIFEST_STORE)) {
          db.createObjectStore(MANIFEST_STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(CONTENT_STORE)) {
          db.createObjectStore(CONTENT_STORE, { keyPath: 'id' });
        }
      },
    });
    return this.dbPromise;
  }

  /**
   * Layer 1: Get manifest (Metadata)
   */
  async getManifest(id: string): Promise<CacheManifest | null> {
    const db = await this.getDB();
    return db.get(MANIFEST_STORE, id);
  }

  /**
   * Layer 2: Get full content from IndexedDB
   */
  async getContent<T>(id: string): Promise<T | null> {
    const db = await this.getDB();
    return db.get(CONTENT_STORE, id);
  }

  /**
   * Combined: Get full cached item
   */
  async get<T>(id: string): Promise<CachedItem<T> | null> {
    const [manifest, content] = await Promise.all([
      this.getManifest(id),
      this.getContent<T>(id)
    ]);

    if (!manifest || !content) return null;
    return { manifest, content };
  }

  /**
   * Set cache item
   */
  async set<T>(manifest: CacheManifest, content: T): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction([MANIFEST_STORE, CONTENT_STORE], 'readwrite');
    
    await Promise.all([
      tx.objectStore(MANIFEST_STORE).put(manifest),
      tx.objectStore(CONTENT_STORE).put(content),
      tx.done
    ]);
  }

  /**
   * Invalidate specific item
   */
  async invalidate(id: string): Promise<void> {
    const db = await this.getDB();
    const tx = db.transaction([MANIFEST_STORE, CONTENT_STORE], 'readwrite');
    await Promise.all([
      tx.objectStore(MANIFEST_STORE).delete(id),
      tx.objectStore(CONTENT_STORE).delete(id),
      tx.done
    ]);
  }

  /**
   * Utility to check if local version is valid
   */
  async isValid(id: string, serverVersion: number, serverChecksum?: string): Promise<boolean> {
    const manifest = await this.getManifest(id);
    if (!manifest) return false;

    if (serverChecksum) {
      return manifest.checksum === serverChecksum;
    }
    
    return manifest.version === serverVersion;
  }

  /**
   * Generates a manifest for a piece of content
   */
  async createManifest(id: string, version: number, type: CacheManifest['type'], content: any, metadata?: any, r2Key?: string): Promise<CacheManifest> {
    const checksum = await computeHash(JSON.stringify(content));
    return {
      id,
      version,
      updatedAt: Date.now(),
      lastValidatedAt: Date.now(),
      checksum,
      type,
      metadata,
      r2Key
    };
  }
}

export const smartCache = new SmartCacheManager();
